import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Modal, notification, Space, Table } from 'antd'
import { deleteDownloadCenterFile, downloadFile, generateOssSignDownloadUrl, getDownloadFileList } from '../../common/request'
import ResponseCode from '../../common/response-code'
import ComponentVisibleByAuth from '../../components/ComponentVisibleByAuth'
import { downloadFile as generateFileAndDownload, downloadBySignedUrl, prettyBytes } from '../../plugins/utils'

function DownloadCenter (props) {
  const [tableData, setTableData] = useState({})
  const [pageNum, setPageNum] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)

  const columns = [
    {
      title: '文件名',
      key: 'fileName',
      dataIndex: 'fileName'
    },
    {
      title: '文件类型',
      key: 'suffix',
      dataIndex: 'suffix'
    },
    {
      title: '文件大小',
      key: 'size',
      dataIndex: 'size',
      render: (record) => {
        return <div>{ prettyBytes(record, 2) }</div>
      }
    },
    {
      title: '状态',
      key: 'status',
      dataIndex: 'status',
      render: (record) => {
        if (record === 0) {
          return <div className={ 'text-primary' }>正在生成</div>
        } else if (record === 1) {
          return <div className={ 'text-success' }>已生成</div>
        } else {
          return <div className={ 'text-danger' }>生成失败</div>
        }
      }
    },
    {
      title: '生成时间',
      key: 'createTime',
      dataIndex: 'createTime'
    },
    {
      title: '操作',
      key: 'operation',
      dataIndex: 'operation',
      render: (record, row) => {
        return (
          <Space className={ 'd-flex justify-content-end' }>
            { row.status === 1 ?
              <Button
                type={ 'primary' }
                ghost
                loading={ downloadingId === row.id }
                onClick={ () => {
                  handleDownload(row)
                } }
              >下载</Button>
              : '' }
            <ComponentVisibleByAuth authCode={ 'button_xzzx-sc' }>
              <Button
                type={ 'primary' }
                ghost
                danger
                onClick={ deleteFile.bind(this, row) }
              >删除</Button>
            </ComponentVisibleByAuth>
          </Space>
        )
      }
    }
  ]

  useEffect(() => {
    loopTableData()
  }, [])

  useEffect(() => {
    getTableData()
  }, [pageSize, pageNum])

  const buildDownloadFileName = (row) => {
    if (!row.suffix) {
      return row.fileName
    }
    const suffix = String(row.suffix).replace(/^\./, '')
    if (String(row.fileName).toLowerCase().endsWith(`.${suffix.toLowerCase()}`)) {
      return row.fileName
    }
    return `${row.fileName}.${suffix}`
  }

  const handleDownload = async (row) => {
    // 兼容旧 FastDFS 路径
    if (row.filePath && row.filePath.indexOf('group1') > -1) {
      setDownloadingId(row.id)
      try {
        await downloadFileAction(row)
      } finally {
        setDownloadingId(null)
      }
      return
    }

    const fileName = buildDownloadFileName(row)
    setDownloadingId(row.id)

    try {
      const res = await generateOssSignDownloadUrl({
        fileKey: row.filePath,
        fileName,
        https: true
      })

      if (res.code !== ResponseCode.success || !res.data?.url) {
        Modal.error({
          title: '错误',
          content: res.msg || '获取下载链接失败'
        })
        return
      }

      try {
        await downloadBySignedUrl(res.data.url, fileName)
      } catch (err) {
        console.error(err)
        await downloadFileAction(row)
      }
    } catch (err) {
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，获取下载链接失败'
      })
    } finally {
      setDownloadingId(null)
    }
  }

  const loopTableData = () => {
    setTimeout(() => {
      getTableData()
    }, 10 * 1000)
  }

  const getTableData = () => {
    const params = {
      orderBy: 'id',
      orderType: 'DESC',
      pageNum,
      pageSize
    }

    setLoading(true)
    getDownloadFileList(params).then(res => {
      setLoading(false)
      if (res.code === ResponseCode.success) {
        setTableData(res.data)
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      setLoading(false)
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，查询文件列表失败'
      })
    })
  }

  const downloadFileAction = async (row) => {
    try {
      const res = await downloadFile({ fileUrl: row.filePath })
      await generateFileAndDownload(res, row.suffix, row.fileName)
    } catch (err) {
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，下载文件失败'
      })
      throw err
    }
  }

  const deleteFile = (row) => {
    Modal.confirm({
      title: '删除文件',
      content: `确定要删除文件【${ row.fileName }】吗？`,
      onOk: () => {
        deleteFileAction(row)
      }
    })
  }

  const deleteFileAction = (row) => {
    setLoading(true)
    deleteDownloadCenterFile({ id: row.id }).then(res => {
      setLoading(false)
      if (res.code == ResponseCode.success) {
        notification.success({
          message: '操作成功'
        })
        getTableData()
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      setLoading(false)
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，删除文件失败'
      })
    })
  }

  const paginationChange = (num, size) => {
    setPageSize(size)
    if (size === pageSize) {
      setPageNum(num)
    } else {
      setPageNum(1)
    }
  }

  return (
    <div>
      <ConfigProvider componentSize={ 'small' }>
        <Table
          loading={loading}
          columns={ columns }
          dataSource={ tableData.elements }
          rowKey={ record => record.id }
          pagination={ {
            showTotal: (total) => {
              return `共 ${ total } 条`
            },
            size: 'normal',
            showSizeChanger: true,
            total: tableData.totalElements,
            pageSize: pageSize,
            current: pageNum,
            onChange: paginationChange
          } }
        ></Table>
      </ConfigProvider>
    </div>
  )
}

export default DownloadCenter
